import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useMeasuredColumnCount } from '../../../../../shared/hooks/useMeasuredColumnCount';

import styles from './VirtualizedGamesGrid.module.css';

const ROW_OVERSCAN = 3;
const ESTIMATED_ROW_HEIGHT = 280;
const INITIAL_LOAD_SIZE = 40;
const CHUNK_SIZE = 20;

const getColumnCount = (width: number): number => {
  if (width >= 1025) return 5;
  if (width >= 576) return 4;
  if (width >= 389) return 3;
  return Math.max(1, Math.floor(width / 140));
};

const getGap = (columnCount: number): number => (columnCount >= 5 ? 10 : 8);

interface VirtualizedGamesGridProps<T> {
  games: T[];
  campaignId: string;
  containerExtraClassName?: string;
  renderGame: (game: T, index: number) => React.ReactNode;
}

export const VirtualizedGamesGrid = <T,>({
  games,
  campaignId,
  containerExtraClassName,
  renderGame,
}: VirtualizedGamesGridProps<T>) => {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [columnCount, setMeasuredNode] = useMeasuredColumnCount(getColumnCount, 5);
  // The grid also needs a stable ref for the scroll-margin effect below, so both are kept
  // in sync off the same DOM node rather than measuring a second, separate element.
  const setGridRef = useCallback(
    (node: HTMLDivElement | null) => {
      parentRef.current = node;
      setMeasuredNode(node);
    },
    [setMeasuredNode],
  );
  const total = games.length;
  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(INITIAL_LOAD_SIZE, total),
  );

  // Reset the visible window when the underlying campaign changes.
  useEffect(() => {
    setVisibleCount(Math.min(INITIAL_LOAD_SIZE, total));
  }, [campaignId, total]);

  const cappedGames = useMemo(
    () => games.slice(0, visibleCount),
    [games, visibleCount],
  );
  const rowCount = Math.ceil(cappedGames.length / columnCount);
  const gap = getGap(columnCount);

  // The grid itself doesn't scroll. Find the nearest scrollable ancestor
  // (e.g. .lobbyContainer) and virtualize against its scroll position.
  // scrollMargin offsets the virtualizer's coordinate space by the grid's
  // position inside that scroll container.
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const grid = parentRef.current;
    if (!grid) return;

    let ancestor: HTMLElement | null = grid.parentElement;
    while (ancestor && ancestor !== document.body) {
      const overflowY = getComputedStyle(ancestor).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      ancestor = ancestor.parentElement;
    }
    const scrollEl = ancestor ?? document.documentElement;
    setScrollElement(scrollEl);

    const updateScrollMargin = () => {
      const gridRect = grid.getBoundingClientRect();
      const scrollRect = scrollEl.getBoundingClientRect();
      // React bails out of the re-render when the value is unchanged.
      setScrollMargin(gridRect.top - scrollRect.top + scrollEl.scrollTop);
    };

    updateScrollMargin();
    const observer = new ResizeObserver(updateScrollMargin);
    observer.observe(scrollEl);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [campaignId, columnCount]);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollElement,
    estimateSize: () => ESTIMATED_ROW_HEIGHT + gap,
    overscan: ROW_OVERSCAN,
    scrollMargin,
  });

  // Recompute layout when the column count flips (so existing rows are remeasured).
  useEffect(() => {
    virtualizer.measure();
  }, [columnCount, virtualizer]);

  // Extend the visible window when the last row enters the overscan range.
  const virtualItems = virtualizer.getVirtualItems();
  const lastRenderedRow = virtualItems[virtualItems.length - 1]?.index ?? -1;
  const hasMore = visibleCount < total;

  useEffect(() => {
    if (hasMore && lastRenderedRow >= rowCount - 1) {
      setVisibleCount((prev) => Math.min(prev + CHUNK_SIZE, total));
    }
  }, [hasMore, lastRenderedRow, rowCount, total]);

  const containerClassName = [styles.container, containerExtraClassName]
    .filter(Boolean)
    .join(' ');

  // Row geometry lives in CSS and reads these two; see .row in the stylesheet.
  const containerStyle = {
    '--columns': columnCount,
    '--gap': `${gap}px`,
  } as React.CSSProperties;

  return (
    <div
      ref={setGridRef}
      className={containerClassName}
      data-campaign-id={campaignId}
      data-games-count={total}
      data-visible-games={cappedGames.length}
      style={containerStyle}
    >
      <div
        className={styles.viewport}
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualItems.map((virtualRow) => {
          const startIdx = virtualRow.index * columnCount;
          const rowGames = cappedGames.slice(startIdx, startIdx + columnCount);

          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              className={styles.row}
              style={{
                transform: `translateY(${virtualRow.start - scrollMargin}px)`,
              }}
            >
              {rowGames.map((game, i) => renderGame(game, startIdx + i))}
            </div>
          );
        })}
      </div>
    </div>
  );
};
